/**
 * Heart Burst Animation Configuration
 * Reusable heart burst animation logic that can be used in any component
 */

// ============ TYPES ============

export interface FloatingHeart {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  delay: number;
  shrinkType?: 'none' | 'tiny' | 'small';
  colorTransition?: {
    from: string;
    to: string;
  };
}

export interface HeartConfig {
  color: string;
  shrinkType?: 'none' | 'tiny' | 'small';
  colorTransition?: {
    from: string;
    to: string;
  };
}

export interface AnimationConfig {
  heartSize?: number;
  xDriftRange?: number;
  yDistanceMin?: number;
  yDistanceMax?: number;
  staggerDelay?: number;
}

// ============ DEFAULT COLORS ============
// Import colors from SCSS file
import heartColors from './HeartColors.module.scss';

export const HEART_COLORS = {
  FILL_1: heartColors.startFill1,
  FILL_2: heartColors.startFill2,
  FILL_3: heartColors.startFill3,
  FILL_4: heartColors.startFill4,
} as const;

const HEART_END_COLORS = {
  FILL_1: heartColors.endFill1,
  FILL_2: heartColors.endFill2,
  FILL_3: heartColors.endFill3,
  FILL_4: heartColors.endFill4,
} as const;

// ============ DEFAULT CONFIGURATION ============

const solidHeart = (color: string, shrinkType: HeartConfig['shrinkType']): HeartConfig => ({
  color,
  shrinkType,
});

const transitioningHeart = (
  from: string,
  to: string,
  shrinkType: HeartConfig['shrinkType']
): HeartConfig => ({
  color: from,
  shrinkType,
  colorTransition: { from, to },
});

// 10 floating hearts + 1 main heart = 11 total (main layout + 1 extra solid heart).
export const DEFAULT_HEART_CONFIGS: HeartConfig[] = [
  solidHeart(HEART_COLORS.FILL_3, 'tiny'),
  solidHeart(HEART_COLORS.FILL_3, 'none'),
  solidHeart(HEART_COLORS.FILL_2, 'none'),
  transitioningHeart(HEART_COLORS.FILL_1, HEART_END_COLORS.FILL_1, 'none'),
  transitioningHeart(HEART_COLORS.FILL_1, HEART_END_COLORS.FILL_1, 'none'),
  transitioningHeart(HEART_COLORS.FILL_1, HEART_END_COLORS.FILL_1, 'small'),
  transitioningHeart(HEART_COLORS.FILL_1, HEART_END_COLORS.FILL_1, 'small'),
  solidHeart(HEART_COLORS.FILL_3, 'small'),
  solidHeart(HEART_COLORS.FILL_4, 'small'),
  solidHeart(HEART_COLORS.FILL_2, 'small'),
];

export const DEFAULT_ANIMATION_CONFIG: AnimationConfig = {
  heartSize: 80,
  xDriftRange: 200,
  yDistanceMin: 50,
  yDistanceMax: 650,
  staggerDelay: 0,
};

// ============ HEART GENERATION ============

/**
 * Generate floating hearts using the default heart and animation configuration.
 */
export const generateFloatingHearts = (): FloatingHeart[] => {
  const {
    heartSize = 80,
    xDriftRange = 200,
    yDistanceMin = 50,
    yDistanceMax = 650,
    staggerDelay = 0,
  } = DEFAULT_ANIMATION_CONFIG;

  const defaultConfig = solidHeart(HEART_COLORS.FILL_1, 'none');

  return Array.from({ length: DEFAULT_HEART_CONFIGS.length }, (_, i) => {
    const config = DEFAULT_HEART_CONFIGS[i] ?? defaultConfig;

    // Random horizontal drift
    const xDrift = (Math.random() - 0.5) * xDriftRange;

    // Random upward distance
    const yDistance = -(yDistanceMin + Math.random() * (yDistanceMax - yDistanceMin));

    return {
      id: i,
      x: xDrift,
      y: yDistance,
      color: config.color,
      size: heartSize,
      delay: i * staggerDelay,
      shrinkType: config.shrinkType ?? 'none',
      colorTransition: config.colorTransition,
    };
  });
};

// ============ UTILITY FUNCTIONS ============

/**
 * Check if heart has color transition
 */
export const hasColorTransition = (heart: FloatingHeart): boolean => {
  return !!heart.colorTransition;
};
